import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, Grid, Paper, ToggleButtonGroup, ToggleButton } from '@mui/material';
import CaseDetailCard from './caseDetailCard';
import apiClient from '../../axiosClient';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import Case from '../../interfaces/case';
import { getCurrentPointer, setCurrentPointerLocalStorage } from '../../utils/commonUtils';
import { loadCurrentCaseID, loadCurrentClientReferenceID } from '../../store/slices/dialerCaseDataSlice';
import { loadCaseCurrentIndex, loadFocusedCaseAllocation } from '../../store/slices/focusedCaseAllocationSlice';
import { INTERACTION_TYPES } from '../../constants/interactionTypes';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import DelightCard from '../baseComponents/delightCard';

interface CaseCarouselProps {
    viewMode: 'grid' | 'carousel';
    setViewMode: (mode: 'grid' | 'carousel') => void;
}

const CaseCarousel: React.FC<CaseCarouselProps> = ({ viewMode, setViewMode }) => {
    const dispatch: AppDispatch = useDispatch();
    const [selectedRow, setSelectedRow] = useState<Case>();
    const [agentReport, setAgentReport] = useState<[]>([]);
    const [caseQueueCompleted, setCaseQueueCompleted] = useState<boolean>(false);
    const { focusedCaseAllocation, caseCurrentIndex } = useSelector((state: RootState) => state.focusedCaseAllocation);

    const updateCaseScore = (score: number) => {
        if (selectedRow) {
            let focusedCaseAllocationArray = [...focusedCaseAllocation];
            const caseIndex = focusedCaseAllocationArray.findIndex(c => c.case_id === selectedRow.case_id);
            if (caseIndex !== -1) {
                const updatedCase = { ...focusedCaseAllocationArray[caseIndex], case_score: score };
                focusedCaseAllocationArray[caseIndex] = updatedCase;
                dispatch(loadFocusedCaseAllocation(focusedCaseAllocationArray));
            }
        }
    };

    const fetchSelectedCase = async () => {
        try {
            const apiUrl = "/api/v1/case/";
            const response = await apiClient.get(apiUrl + focusedCaseAllocation[caseCurrentIndex].case_id);
            if (response.status === 200) {
                const record = Case.initialiseCase([response.data]);
                setSelectedRow(record[0]);
                dispatch(loadCurrentCaseID(record[0].case_id));
                dispatch(loadCurrentClientReferenceID(record[0].client_reference_id));
            } else {
                alert("Could not get case detail: " + response.data.message);
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        }
    }

    const fetchAgentReport = async () => {
        try {
            const apiUrl = "/api/v1/interaction/agent_today_report";
            const response = await apiClient.get(apiUrl);
            if (response.status === 200) {
                setAgentReport(response.data.agent_report);
            }
        } catch (error: any) {
            console.error("error while fetching agent report ", error)
        }
    }

    useEffect(() => {
        fetchAgentReport();

        if (focusedCaseAllocation.length === 0) {
            // No cases available
            setCaseQueueCompleted(true);
            return;
        }

        const currentIndex = getCurrentPointer();
        const currentCase = focusedCaseAllocation[currentIndex];

        if (!currentCase || currentCase.case_score >= 3) {
            // Current case already completed → move to next
            setNextPointer();
        }
    }, []);


    useEffect(() => {
        focusedCaseAllocation.length > 0 && caseCurrentIndex >= 0 && fetchSelectedCase();
    }, [caseCurrentIndex, focusedCaseAllocation])

    const setNextPointer = () => {
        const nextPointer = (getCurrentPointer() + 1) % focusedCaseAllocation.length;
        const indices = Array(focusedCaseAllocation.length).fill(0).map((_, i) => (nextPointer + i) % focusedCaseAllocation.length);
        const foundIndex = indices.find(index => focusedCaseAllocation[index]?.case_score < 3);
        if (foundIndex !== undefined) {
            setCurrentPointerLocalStorage(foundIndex);
            dispatch(loadCaseCurrentIndex(foundIndex));
        } else {
            // No case left with case_score < 3
            setCaseQueueCompleted(true);
        }
    };

    const updateFocusMode = (agent_report: any) => {
        setNextPointer();
        setAgentReport(agent_report);
    }

    return (
        <Box sx={{ width: '90vw', height: '60vh', margin: '0 auto', textAlign: 'left' }}>
            <Box sx={{ width: "100%" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="h6">Today's Report</Typography>
                    <Typography variant="body2" color="text.secondary">Total interactions: <strong>{(agentReport as any)['total_interactions'] ?? 0}</strong></Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 2 }}>
                    <Grid container spacing={2}>
                        {INTERACTION_TYPES.map((type) => (
                            <Grid key={type} >
                                <Paper
                                    elevation={1}
                                    sx={{
                                        p: 1,
                                        borderRadius: 2,
                                        minHeight: 16,
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    <Box display='flex' alignItems='center' gap='4px'>
                                        <Typography variant="caption" color="text.secondary">{type} :</Typography>
                                        <Typography variant='body2'>{(agentReport as any)[type] ?? 0}</Typography>
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                <Box width='100%' sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="button" alignItems='center' color='secondary'>
                        Case {focusedCaseAllocation.length === 0 ? 0 : (caseCurrentIndex + 1)} of {focusedCaseAllocation.length}
                    </Typography>
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(_, val) => {
                            if (!val) return;
                            setViewMode(val);
                        }}
                        size="small"
                        aria-label="view mode"
                    >
                        <ToggleButton value="grid" aria-label="grid view">
                            <GridViewIcon />
                        </ToggleButton>
                        <ToggleButton value="carousel" aria-label="carousel view">
                            <ViewCarouselIcon />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                {!caseQueueCompleted ? (
                    <>
                        {/* Main Card */}
                        <Card style={{ marginBottom: "20px", width: '100%' }}>
                            {selectedRow && (
                                <CaseDetailCard
                                    selectedRow={selectedRow}
                                    updateFocusMode={updateFocusMode}
                                    updateCaseScore={updateCaseScore}
                                />
                            )}
                        </Card>
                    </>
                ) : (
                    <DelightCard />
                )}
            </Box>
        </Box>
    );
};

export default CaseCarousel;
